import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowLeft, Share2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPostPage = () => {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchPost();
    }, [slug]);

    const fetchPost = async () => {
        try {
            const response = await axios.get(`${API}/blog/posts/${slug}`);
            setPost(response.data);
        } catch (err) {
            // Use placeholder data if post not found
            setPost({
                title: 'Essential Tax Planning Strategies for UK Businesses',
                slug: slug,
                excerpt: 'Discover effective tax planning strategies.',
                content: `
                    <h2>Understanding Tax Planning</h2>
                    <p>Tax planning is a crucial aspect of running a successful business in the UK. By understanding the available reliefs and allowances, you can significantly reduce your tax liability while remaining fully compliant with HMRC regulations.</p>
                    
                    <h2>Key Strategies for 2024</h2>
                    <p>Here are some essential tax planning strategies that every UK business should consider:</p>
                    
                    <h3>1. Maximize Capital Allowances</h3>
                    <p>Capital allowances allow you to deduct the cost of certain assets from your profits before calculating your tax. This includes the Annual Investment Allowance (AIA), which currently allows 100% relief on qualifying plant and machinery investments up to £1 million.</p>
                    
                    <h3>2. Pension Contributions</h3>
                    <p>Employer pension contributions are fully deductible as a business expense. This is one of the most tax-efficient ways to extract value from your company while building your retirement fund.</p>
                    
                    <h3>3. R&D Tax Credits</h3>
                    <p>If your business is involved in innovation or developing new products, processes, or services, you may be eligible for R&D tax credits. These can provide substantial tax relief or even cash credits for SMEs.</p>
                    
                    <h2>Planning Ahead</h2>
                    <p>Effective tax planning requires looking ahead and making decisions at the right time. Don't wait until your year-end to think about tax - proactive planning throughout the year yields the best results.</p>
                    
                    <blockquote>
                    "The best time to start tax planning is at the beginning of your financial year, not the end."
                    </blockquote>
                    
                    <h2>Get Professional Advice</h2>
                    <p>Every business is unique, and what works for one may not be suitable for another. At Hope Accountancy, we provide tailored tax planning advice to help you minimize your tax liability legally and ethically.</p>
                `,
                image_url: 'https://images.unsplash.com/photo-1631248082035-507d0fe8a8e2?crop=entropy&cs=srgb&fm=jpg&q=85',
                author: 'Hope Accountancy',
                created_at: '2024-01-15T10:00:00Z'
            });
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const handleShare = () => {
        if (navigator.share) {
            navigator.share({
                title: post?.title,
                url: window.location.href
            });
        } else {
            navigator.clipboard.writeText(window.location.href);
            alert('Link copied to clipboard!');
        }
    };

    if (loading) {
        return (
            <main className="pt-32 pb-20 bg-hope-light min-h-screen">
                <div className="container-hope">
                    <div className="max-w-4xl mx-auto">
                        <div className="h-8 w-32 skeleton rounded-lg mb-8" />
                        <div className="h-12 skeleton rounded-lg mb-4" />
                        <div className="h-6 w-1/2 skeleton rounded-lg mb-8" />
                        <div className="h-96 skeleton rounded-3xl mb-8" />
                        <div className="space-y-4">
                            <div className="h-4 skeleton rounded-lg" />
                            <div className="h-4 skeleton rounded-lg" />
                            <div className="h-4 w-3/4 skeleton rounded-lg" />
                        </div>
                    </div>
                </div>
            </main>
        );
    }

    if (error || !post) {
        return (
            <main className="pt-32 pb-20 bg-hope-light min-h-screen">
                <div className="container-hope text-center">
                    <h1 className="text-2xl font-heading font-bold text-hope-forest mb-4">
                        Article Not Found
                    </h1>
                    <Link to="/blog" className="text-hope-emerald hover:underline">
                        ← Back to Blog
                    </Link>
                </div>
            </main>
        );
    }

    return (
        <main data-testid="blog-post-page" className="bg-hope-light">
            {/* Hero Image */}
            <section className="relative h-[50vh] min-h-[400px]">
                <img
                    src={post.image_url || 'https://images.unsplash.com/photo-1631248082035-507d0fe8a8e2?crop=entropy&cs=srgb&fm=jpg&q=85'}
                    alt={post.title}
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-hope-forest/90 via-hope-forest/50 to-transparent" />
                
                <div className="absolute bottom-0 left-0 right-0 pb-12">
                    <div className="container-hope">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="max-w-4xl"
                        >
                            <Link 
                                to="/blog" 
                                data-testid="back-to-blog"
                                className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
                            >
                                <ArrowLeft className="w-4 h-4" /> Back to Blog
                            </Link>
                            <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-4">
                                {post.title}
                            </h1>
                            <div className="flex flex-wrap items-center gap-6 text-white/70">
                                <span className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4" />
                                    {formatDate(post.created_at)}
                                </span>
                                <span className="flex items-center gap-2">
                                    <User className="w-4 h-4" />
                                    {post.author}
                                </span>
                                <button
                                    onClick={handleShare}
                                    data-testid="share-btn"
                                    className="flex items-center gap-2 hover:text-white transition-colors"
                                >
                                    <Share2 className="w-4 h-4" />
                                    Share
                                </button>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="section-padding">
                <div className="container-hope">
                    <motion.article
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="max-w-4xl mx-auto bg-white rounded-3xl p-8 md:p-12 shadow-lg"
                    >
                        <div 
                            className="prose-hope"
                            dangerouslySetInnerHTML={{ __html: post.content }}
                        />
                    </motion.article>

                    {/* CTA */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="max-w-4xl mx-auto mt-12 text-center"
                    >
                        <div className="bg-hope-emerald rounded-3xl p-8 md:p-12">
                            <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-4">
                                Need Help With Your Accounts?
                            </h2>
                            <p className="text-white/80 mb-6 max-w-xl mx-auto">
                                Our team of experts is ready to help you with all your accounting needs.
                            </p>
                            <Link
                                to="/contact"
                                data-testid="blog-cta-btn"
                                className="btn-premium bg-white text-hope-emerald hover:bg-hope-light inline-block"
                            >
                                Get Free Consultation
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </main>
    );
};

export default BlogPostPage;
